import { useCallback, useEffect, useState, type ReactElement } from "react";
import { useParams } from "react-router-dom";
import { usePortal, findStage } from "../state/PortalContext.js";
import { PageHelp } from "../layout/PageHelp.js";

interface ParameterMetadata {
  name: string;
  description: string;
  hypothesis: string;
  tuningGuidance: string;
  unit: string;
  minValue: number;
  maxValue: number;
  defaultValue: number;
}

interface ScoringPolicy {
  version: string;
  updatedAt: number;
  updatedBy: string;
  changeReason: string;
  parameters: Record<string, number>;
  activeExperiment?: {
    experimentId: string;
    name: string;
    description: string;
    enabled: boolean;
    trafficAllocation: Record<string, number>;
    variants: Record<string, Record<string, number>>;
  } | null;
}

interface SimulationOutput {
  scenarioName: string;
  baselineScore: number;
  simulatedScore: number;
  scoreDelta: number;
  policyVersion: string;
  activeVariant: string;
  breakdown: Record<string, number>;
}

export function IntelligencePage(): ReactElement {
  const { api, catalog } = usePortal();
  const params = useParams();
  const serviceId = params.service!;
  const stageId = params.stage!;
  const stage = findStage(catalog, serviceId, stageId);

  const [activeTab, setActiveTab] = useState<"parameters" | "experiments" | "simulation">("parameters");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [policy, setPolicy] = useState<ScoringPolicy | null>(null);
  const [metadata, setMetadata] = useState<Record<string, ParameterMetadata>>({});
  const [editedParams, setEditedParams] = useState<Record<string, number>>({});
  const [changeReason, setChangeReason] = useState("");
  const [confirmStageInput, setConfirmStageInput] = useState("");
  const [isConflict, setIsConflict] = useState(false);

  // Simulation state
  const [simScenario, setSimScenario] = useState<"explorer" | "swiper" | "deficit" | "reciprocal">("deficit");
  const [simRunning, setSimRunning] = useState(false);
  const [simResult, setSimResult] = useState<SimulationOutput | null>(null);

  const loadPolicy = useCallback(async () => {
    setLoading(true);
    setError(null);
    setIsConflict(false);
    try {
      const res = (await api.intelligencePolicy(serviceId, stageId)) as {
        policy: ScoringPolicy;
        metadata: Record<string, ParameterMetadata>;
      };
      setPolicy(res.policy);
      setMetadata(res.metadata);
      setEditedParams({ ...res.policy.parameters });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load discovery policy");
    } finally {
      setLoading(false);
    }
  }, [api, serviceId, stageId]);

  useEffect(() => {
    void loadPolicy();
  }, [loadPolicy]);

  function handleParamChange(key: string, value: number) {
    setEditedParams((prev) => ({ ...prev, [key]: value }));
    setSuccessMsg(null);
  }

  function handleReset() {
    if (policy) {
      setEditedParams({ ...policy.parameters });
      setChangeReason("");
      setSuccessMsg(null);
    }
  }

  async function handleSave() {
    if (!policy) return;
    if (!changeReason.trim()) {
      setError("A change reason / hypothesis is required to update discovery policy.");
      return;
    }
    if (stage?.protected && confirmStageInput !== stageId) {
      setError(`Protected stage: please type "${stageId}" in the confirmation box.`);
      return;
    }

    setSaving(true);
    setError(null);
    setIsConflict(false);
    setSuccessMsg(null);
    try {
      const payload = {
        ...policy,
        expectedVersion: policy.version,
        changeReason: changeReason.trim(),
        parameters: editedParams,
        confirmStage: stage?.protected ? confirmStageInput : undefined,
      };
      const res = (await api.updateIntelligencePolicy(serviceId, stageId, payload)) as {
        status: string;
        version: string;
        policy: ScoringPolicy;
      };
      setPolicy(res.policy);
      setEditedParams({ ...res.policy.parameters });
      setChangeReason("");
      setConfirmStageInput("");
      setSuccessMsg(`Policy updated successfully to version ${res.version}.`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to save policy";
      if (msg.toLowerCase().includes("conflict") || msg.includes("409")) {
        setIsConflict(true);
        setError("Concurrent modification detected: The discovery policy was updated by another administrator while you were making changes. Please reload the latest policy to review and reapply your updates.");
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  }


  async function runSimulation() {
    setSimRunning(true);
    setError(null);
    try {
      const scenarioMap = {
        explorer: {
          targetUserId: "user-explorer-1",
          rawInteractions: [
            { type: "profile_view", durationMs: 9500, photoIndex: 4, expandedBio: true, promptsRead: 3 },
            { type: "swipe_like", durationMs: 4500 },
          ],
        },
        swiper: {
          targetUserId: "user-swiper-2",
          rawInteractions: [
            { type: "profile_view", durationMs: 300, photoIndex: 0, expandedBio: false, promptsRead: 0 },
            { type: "swipe_like", durationMs: 150 },
          ],
        },
        deficit: {
          targetUserId: "user-deficit-3",
          rawInteractions: [{ type: "profile_view", durationMs: 3500 }],
          exposureDeficitRatio: 0.25, // only received 25% of expected impressions
        },
        reciprocal: {
          targetUserId: "user-reciprocal-4",
          rawInteractions: [{ type: "swipe_like", durationMs: 3000 }, { type: "chat_reply", replyLatencySeconds: 45 }],
          reciprocalAttractionScore: 0.9,
          isPrimeTime: true,
        },
      };

      const scenario = scenarioMap[simScenario];
      const res = (await api.simulateIntelligence(serviceId, stageId, {
        scenario,
        overrides: editedParams,
      })) as SimulationOutput;
      setSimResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Simulation failed");
    } finally {
      setSimRunning(false);
    }
  }

  const isDirty = policy && Object.keys(editedParams).some((k) => editedParams[k] !== policy.parameters[k]);

  if (loading) return <p role="status">Loading discovery policy…</p>;

  return (
    <section aria-labelledby="intelligence-title" style={{ maxWidth: 960 }}>
      <header style={{ marginBottom: 16 }}>
        <h1 id="intelligence-title" style={{ margin: "0 0 4px 0" }}>
          Discovery & Scoring Policy
        </h1>
        <p style={{ color: "#5f6368", margin: 0 }}>
          Inspect, tune, and test the scientific formulas and fair exposure boosts that rank profiles for user discovery.
        </p>
      </header>

      {stage?.protected ? (
        <div style={{ background: "#fce8e6", border: "1px solid #c5221f", color: "#c5221f", padding: "8px 12px", borderRadius: 4, marginBottom: 16 }}>
          <strong>Protected Stage ({stage.label}):</strong> Parameter updates take effect across all live recommendations. Adjustments require an explicit change justification and stage confirmation.
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 12, borderBottom: "2px solid #e0e0e0", marginBottom: 16 }}>
        <button
          type="button"
          onClick={() => setActiveTab("parameters")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "parameters" ? "3px solid #0b57d0" : "3px solid transparent",
            padding: "8px 16px",
            fontWeight: activeTab === "parameters" ? 700 : 500,
            cursor: "pointer",
          }}
        >
          Algorithm Parameters
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("experiments")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "experiments" ? "3px solid #0b57d0" : "3px solid transparent",
            padding: "8px 16px",
            fontWeight: activeTab === "experiments" ? 700 : 500,
            cursor: "pointer",
          }}
        >
          A/B Experiments
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("simulation")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "simulation" ? "3px solid #0b57d0" : "3px solid transparent",
            padding: "8px 16px",
            fontWeight: activeTab === "simulation" ? 700 : 500,
            cursor: "pointer",
          }}
        >
          Simulation Sandbox
        </button>
      </div>

      {error ? (
        <div role="alert" style={{ background: "#fdeded", color: "#5f2120", padding: "10px 14px", borderRadius: 4, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <span>{error}</span>
            {isConflict ? (
              <button
                type="button"
                onClick={() => void loadPolicy()}
                style={{
                  background: "#d32f2f",
                  color: "#fff",
                  border: "none",
                  padding: "4px 10px",
                  borderRadius: 4,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Reload Latest Policy
              </button>
            ) : null}
          </div>
        </div>
      ) : null}


      {successMsg ? (
        <div role="status" style={{ background: "#edf7ed", color: "#1e4620", padding: "8px 12px", borderRadius: 4, marginBottom: 16 }}>
          {successMsg}
        </div>
      ) : null}

      {activeTab === "parameters" && policy ? (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
            <span style={{ fontSize: 13, color: "#5f6368" }}>
              Active Policy Version: <strong>{policy.version}</strong> (Last updated by <code>{policy.updatedBy}</code>)
            </span>
            {isDirty ? (
              <span style={{ background: "#fff0c2", color: "#7a4100", padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                Unsaved Changes
              </span>
            ) : null}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
            {Object.entries(editedParams).map(([key, val]) => {
              const meta = metadata[key];
              const isParamDirty = policy.parameters[key] !== val;
              const min = meta?.minValue ?? 0;
              const max = meta?.maxValue ?? 2.5;
              const step = max <= 1.0 ? 0.05 : 0.05;

              return (
                <div
                  key={key}
                  style={{
                    border: isParamDirty ? "2px solid #0b57d0" : "1px solid #dadce0",
                    borderRadius: 8,
                    padding: 16,
                    background: isParamDirty ? "#f8fafd" : "#fff",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div>
                      <strong style={{ fontSize: 15 }}>{meta?.name ?? key}</strong>
                      <span style={{ marginLeft: 8, fontSize: 12, color: "#5f6368" }}>({meta?.unit ?? "value"})</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="number"
                        min={min}
                        max={max}
                        step={step}
                        value={val}
                        onChange={(e) => handleParamChange(key, parseFloat(e.target.value) || 0)}
                        style={{ width: 80, padding: 4, textAlign: "right", fontWeight: 600 }}
                      />
                    </div>
                  </div>

                  <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={val}
                    onChange={(e) => handleParamChange(key, parseFloat(e.target.value))}
                    style={{ width: "100%", margin: "8px 0" }}
                  />

                  {meta ? (
                    <div style={{ fontSize: 13, color: "#3c4043", marginTop: 4 }}>
                      <p style={{ margin: "2px 0" }}>{meta.description}</p>
                      <p style={{ margin: "2px 0", color: "#137333" }}>
                        <strong>Hypothesis:</strong> {meta.hypothesis}
                      </p>
                      <p style={{ margin: "2px 0", color: "#5f6368" }}>
                        <strong>Tuning advice:</strong> {meta.tuningGuidance}
                      </p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 24, padding: 16, background: "#f8f9fa", borderRadius: 8, border: "1px solid #dadce0" }}>
            <h3 style={{ margin: "0 0 8px 0", fontSize: 15 }}>Deploy Policy Changes</h3>
            <div style={{ marginBottom: 12 }}>
              <label htmlFor="change-reason" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                Change Reason & Hypothesis (Audit Record) *
              </label>
              <input
                id="change-reason"
                type="text"
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder="e.g. Boost exposure for active users to decrease loneliness metric by 10%"
                style={{ width: "100%", padding: "8px 10px", borderRadius: 4, border: "1px solid #ccc" }}
              />
            </div>

            {stage?.protected ? (
              <div style={{ marginBottom: 12 }}>
                <label htmlFor="confirm-stage" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4, color: "#c5221f" }}>
                  Confirm Protected Stage: Type "{stageId}" to confirm *
                </label>
                <input
                  id="confirm-stage"
                  type="text"
                  value={confirmStageInput}
                  onChange={(e) => setConfirmStageInput(e.target.value)}
                  style={{ width: 160, padding: "6px 8px", borderRadius: 4, border: "1px solid #c5221f" }}
                />
              </div>
            ) : null}

            <div style={{ display: "flex", gap: 12 }}>
              <button
                type="button"
                onClick={handleSave}
                disabled={!isDirty || saving || !changeReason.trim()}
                style={{
                  background: "#0b57d0",
                  color: "#fff",
                  border: "none",
                  borderRadius: 4,
                  padding: "8px 16px",
                  fontWeight: 600,
                }}
              >
                {saving ? "Deploying…" : "Save & Deploy Policy"}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={!isDirty || saving}
                style={{
                  background: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  padding: "8px 16px",
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === "experiments" && policy ? (
        <div>
          <PageHelp>
            <p>
              A/B Experiments partition active users deterministically into variants using <code>SHA-256(userId + experimentId) % 100</code>.
              This guarantees that a candidate sees consistent matching behavior without stateful session cookies or cross-cloud latency.
            </p>
          </PageHelp>

          <div style={{ background: "#fff", border: "1px solid #dadce0", borderRadius: 8, padding: 16 }}>
            <h3>Active Experiment Status</h3>
            {policy.activeExperiment?.enabled ? (
              <div>
                <p><strong>Experiment ID:</strong> <code>{policy.activeExperiment.experimentId}</code></p>
                <p><strong>Name:</strong> {policy.activeExperiment.name}</p>
                <p><strong>Description:</strong> {policy.activeExperiment.description}</p>
                <h4>Traffic Split</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Variant</th>
                      <th>Traffic %</th>
                      <th>Overrides</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(policy.activeExperiment.trafficAllocation).map(([variant, pct]) => (
                      <tr key={variant}>
                        <td><code>{variant}</code></td>
                        <td>{pct}%</td>
                        <td>
                          {variant === "control"
                            ? "Baseline Parameters"
                            : JSON.stringify(policy.activeExperiment?.variants[variant] ?? {})}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: "#5f6368" }}>No active A/B experiment currently running. All traffic uses the calibrated baseline policy.</p>
            )}
          </div>
        </div>
      ) : null}

      {activeTab === "simulation" ? (
        <div>
          <PageHelp>
            <p>
              The simulation sandbox lets data scientists test proposed weight adjustments against archetypal dater behaviors before deploying them into production.
            </p>
          </PageHelp>

          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
            <label htmlFor="sim-scenario" style={{ fontWeight: 600 }}>Select Test Scenario:</label>
            <select
              id="sim-scenario"
              value={simScenario}
              onChange={(e) => setSimScenario(e.target.value as any)}
              style={{ padding: "6px 12px", borderRadius: 4 }}
            >
              <option value="deficit">Unnoticed Active User (Low Impression Count)</option>
              <option value="explorer">Deliberate Profile Explorer (High Dwell & Read Depth)</option>
              <option value="swiper">Speed Swiper (Rapid reflex swiping without reading)</option>
              <option value="reciprocal">Prime-Time Mutual Spark (High Reciprocity at Peak Hours)</option>
            </select>
            <button
              type="button"
              onClick={runSimulation}
              disabled={simRunning}
              style={{
                background: "#0b57d0",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "8px 16px",
                fontWeight: 600,
              }}
            >
              {simRunning ? "Simulating…" : "Run Simulation"}
            </button>
          </div>

          {simResult ? (
            <div style={{ background: "#fff", border: "1px solid #dadce0", borderRadius: 8, padding: 16 }}>
              <h3>Simulation Results: {simResult.scenarioName}</h3>
              <div style={{ display: "flex", gap: 32, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 12, color: "#5f6368" }}>Baseline Score</div>
                  <div style={{ fontSize: 24, fontWeight: 700 }}>{simResult.baselineScore.toFixed(3)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#5f6368" }}>Simulated Score</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: simResult.scoreDelta >= 0 ? "#137333" : "#c5221f" }}>
                    {simResult.simulatedScore.toFixed(3)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#5f6368" }}>Net Difference</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: simResult.scoreDelta >= 0 ? "#137333" : "#c5221f" }}>
                    {simResult.scoreDelta >= 0 ? `+${simResult.scoreDelta.toFixed(3)}` : simResult.scoreDelta.toFixed(3)}
                  </div>
                </div>
              </div>

              <h4>Contribution Breakdown</h4>
              <table>
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Sub-Score Contribution</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(simResult.breakdown).map(([comp, score]) => (
                    <tr key={comp}>
                      <td><code>{comp}</code></td>
                      <td>{score.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
